import { IPartRequestVehicleMatch, PartRequestMatchStrength } from "../../models/partRequests/IPartRequest";
import { VehicleStatus } from "../../models/Vehicle";
import classNames from "../../utils/classNames";
import openInNewTab from "../../utils/openInNewTab";
import { PART_REQUEST_MATCH_LABELS } from "../../utils/partRequests";

interface MatchedVehiclesProps {
  matches: IPartRequestVehicleMatch[];
  /** Chips only (list rows) or a fuller line per vehicle (detail page). */
  compact?: boolean;
  /** When set, clicking a chip selects the vehicle instead of opening it. */
  onSelect?: (match: IPartRequestVehicleMatch) => void;
  selectedVehicleId?: string | null;
}

const strengthClasses: Record<PartRequestMatchStrength, string> = {
  [PartRequestMatchStrength.Exact]: "bg-green-50 text-green-800 ring-green-300",
  [PartRequestMatchStrength.Likely]: "bg-blue-50 text-blue-800 ring-blue-200",
  [PartRequestMatchStrength.Possible]: "bg-gray-50 text-gray-700 ring-gray-200",
  [PartRequestMatchStrength.None]: "bg-gray-50 text-gray-500 ring-gray-200",
};

export default function MatchedVehicles({ matches, compact = false, onSelect, selectedVehicleId }: MatchedVehiclesProps) {
  if (matches.length === 0) {
    return <span className="text-sm text-gray-400">{compact ? "No match in stock" : "Nothing in the yard matches this vehicle."}</span>;
  }

  return (
    <div className={classNames("flex flex-wrap gap-1.5", compact ? "" : "gap-2")}>
      {matches.map((match) => {
        const label = [match.vrm, match.year, match.make, match.model].filter(Boolean).join(" · ");
        const selected = selectedVehicleId === match.vehicleId;
        return (
          <button
            key={match.vehicleId}
            type="button"
            title={`${PART_REQUEST_MATCH_LABELS[match.strength]} match · ${VehicleStatus[match.status]}`}
            onClick={(e) => {
              e.stopPropagation();
              if (onSelect) onSelect(match);
              else openInNewTab(`/vehicles/${match.vehicleId}`);
            }}
            className={classNames(
              "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset hover:opacity-80",
              strengthClasses[match.strength],
              selected ? "ring-2 ring-primary-500" : ""
            )}
          >
            <span className="font-semibold">{PART_REQUEST_MATCH_LABELS[match.strength]}</span>
            <span className={compact ? "whitespace-nowrap" : ""}>{label}</span>
            {!compact && match.stockNumber && <span className="text-gray-500">#{match.stockNumber}</span>}
            {!compact && <span className="text-gray-500">{VehicleStatus[match.status]}</span>}
          </button>
        );
      })}
    </div>
  );
}
